// UI strings for English and Hebrew
// Local defaults are used immediately; remote overrides are fetched from Supabase
// and cached in localStorage. Edit strings in Supabase dashboard (ui_strings table)
// for instant updates without redeploying.

const defaultStrings = {
  en: {
    // Header
    login: "Login",
    logOut: "Log out",
    manageUsers: "Manage Users",
    filterByKeyword: "Free text search",
    searchByGeotagger: "Search by geotagger",
    closeSearchPanel: "Close search panel",
    openSearchPanel: "Open search panel",
    geotagPhotos: "Geotag photos",
    externalApi: "External API",
    showQuestionableOnly: "Show questionable only",
    showAllItems: "Show all items",
    findReplace: "Find & Replace in DB",
    goToAddress: "Go to address...",
    historicalMaps: "Historical maps",
    hideAllHistoricalMaps: "Hide all historical maps",
    spotlightMode: "Spotlight mode",
    exitSpotlightMode: "Exit spotlight mode",
    satellitePhoto: "Satellite photo",
    loadingGeotaggedCount: "Loading geotagged count...",
    currentGeotaggedCount: "Current number of geotagged items: {count}.",
    geotaggerSearch: "Geotagger search",

    // Item modal
    noCommentsYet: "No comments yet",
    yourComment: "Your comment…",
    logInToComment: "Log in to comment",
    addComment: "Add comment",
    removeQuestionableFlag: "Remove questionable flag",
    flagAsQuestionable: "Flag as questionable location",
    questionableLocation: "Questionable location",
    markAsUnfit: "Mark as unfit for tagging",
    markAsTaggable: "Mark as taggable",
    unfitForTagging: "Unfit for tagging",
    editLocation: "Edit location",
    removeLocation: "Remove location",
    addDescription: "+ Add description",
    itemAwaitsGeotagging: "This item still awaits geotagging",
    viewSourceLeiden: "View source at the Leiden University Libraries website",
    viewSourceLoc: "View source at the Library of Congress website",
    viewSourceTaArchive: "View source at the Tel Aviv-Yafo Municipal Archive website (search by item code below)",
    viewSourceTaArchiveDirect: "View source at the Tel Aviv-Yafo Municipal Archive website",
    viewSourceNationaalArchief: "View source at the Nationaal Archief website",
    archiveItemCode: "Archive item code: {code}",
    copyrightAleksandrowicz: "© Ze'ev Aleksandrowicz Estate, Tel Aviv; Scans by The National Library of Israel",
    copyrightTaycm: "Courtesy of the Tel Aviv-Yafo City Museum Archive",
    itemId: "ID: {id}",
    firstGeotaggedBy: "First geotagged by {username}",
    lastGeotaggedBy: "Last geotagged by {username}",
    removeGeotagConfirm: "Remove the geotag from this item? It will be moved back to the unlocated list.",
    removeLocationError: "Failed to remove location: {error}",
    saveError: "Failed: {error}",
    failedToSaveTitle: "Failed to save title: {error}",
    failedToSaveDescription: "Failed to save description: {error}",

    // Homeless widget
    photosForGeotagging: "Photos for geotagging",
    exitSelectMode: "Exit select mode",
    selectMultiple: "Select multiple",
    taggable: "Taggable",
    unfit: "Unfit",
    searching: "Searching…",
    searchResultCount: "{count} results",
    selectAll: "Select all",
    deselectAll: "Deselect all",
    restoreToTaggable: "Restore to taggable",
    markAsUnfitForTagging: "Mark as unfit for tagging",
    addLocation: "Add location",

    // Search widget
    searchResults: "Search Results",
    itemsFound: "{count} item{s} found",
    pages: "{current}/{total} pages",
    batchOf: "Batch {current} of {total}",
    itemsRange: "Items {from}–{to} of {total}",

    // Raster widget
    historicalMapsAndImagery: "Historical maps and aerial imagery",

    // Find & Replace
    findReplaceTitle: "Find & Replace in Database",
    column: "Column",
    allFields: "All fields",
    find: "Find",
    replaceWith: "Replace with",
    search: "Search",
    selectPage: "Select page",
    deselectPage: "Deselect page",
    prev: "Prev",
    next: "Next",
    field: "Field",
    value: "Value",
    replaceThisRow: "Replace this row",
    replaceSelected: "Replace selected ({count})",
    close: "Close",
    noMatchesFound: "No matches found for \"{text}\"{inField}.",
    inField: " in {field}",
    replacedRows: "Replaced {ok} rows{fail}. {remaining}",
    failedRows: ", {count} failed",
    stillMatching: "{count} still matching.",
    noMatchesRemaining: "No matches remaining.",
    matchesCount: "{count} matches",
    selectedCount: "{count} selected",
    confirmReplace: "Replace \"{find}\" → \"{replace}\" in {count} selected rows?",

    // Position prompt
    confirm: "Confirm",
    suggestOnly: "Suggest only",
    saveSuggestUncertain: "Save location but flag as uncertain",

    // Unified site
    allCreators: "All creators",
    creatorFilter: "Filter by creator",
    yearRangeFilter: "Filter by year",
    clearYearRange: "Clear year filter",
    unifiedTitle: "Geotagging Visual History",
    aboutTooltip: "About this site",

    // Terms of use (About dialog). Paragraphs split on \n; {email} becomes a mailto link.
    termsOfUseTitle: "Terms of use",
    termsOfUseText: "The location data on this site — geotags, viewing directions, addresses, place identifications, translations and other annotations — is the work of the MAQOM project and its volunteer contributors.\nYou are welcome to browse the site for personal, educational and research purposes. Automated or bulk downloading of the data is not permitted. This includes scraping, systematic querying of the site's map or search interfaces, and any other method of extracting the data in quantity.\nReusing the data, in whole or in part, outside this site — including republishing it, redistributing it, or incorporating it into another app, website, product or dataset — requires prior written permission. To request permission, write to {email}.\nThe photographs themselves remain subject to the terms of the institutions that hold them.",
    termsContactEmail: "oraleks@technion.ac.il",
  },

  he: {
    login: "כניסה",
    logOut: "התנתקות",
    manageUsers: "ניהול משתמשים",
    filterByKeyword: "חיפוש טקסט חופשי",
    searchByGeotagger: "חיפוש לפי מתייג",
    closeSearchPanel: "סגירת חלונית החיפוש",
    openSearchPanel: "פתיחת חלונית החיפוש",
    geotagPhotos: "תיוג גיאוגרפי",
    externalApi: "יבוא ממאגר חיצוני",
    showQuestionableOnly: "הצגת מיקומים לא ודאיים בלבד",
    showAllItems: "הצגת כל הפריטים",
    findReplace: "חיפוש והחלפה במסד הנתונים",

    // Find & Replace
    findReplaceTitle: "חיפוש והחלפה במסד הנתונים",
    column: "עמודה",
    allFields: "כל השדות",
    find: "חפש",
    replaceWith: "החלף ב",
    search: "חיפוש",
    selectPage: "בחירת עמוד",
    deselectPage: "ביטול בחירת עמוד",
    selectAll: "בחירת הכול",
    deselectAll: "ביטול בחירת הכול",
    prev: "הקודם",
    next: "הבא",
    field: "שדה",
    value: "ערך",
    replaceThisRow: "החלפת שורה זו",
    replaceSelected: "החלפת נבחרים ({count})",
    close: "סגור",
    noMatchesFound: "לא נמצאו תוצאות עבור \"{text}\"{inField}.",
    inField: " בשדה {field}",
    replacedRows: "הוחלפו {ok} שורות{fail}. {remaining}",
    failedRows: ", {count} נכשלו",
    stillMatching: "{count} עדיין תואמים.",
    noMatchesRemaining: "אין תוצאות נותרות.",
    matchesCount: "{count} תוצאות",
    selectedCount: "{count} נבחרו",
    confirmReplace: "להחליף \"{find}\" ← \"{replace}\" ב-{count} שורות נבחרות?",
    goToAddress: "חיפוש כתובת",
    historicalMaps: "מפות היסטוריות",
    hideAllHistoricalMaps: "הסתרת כל המפות ההיסטוריות",
    spotlightMode: "מצב זרקור",
    exitSpotlightMode: "יציאה ממצב זרקור",
    satellitePhoto: "תצלום לוויין",
    loadingGeotaggedCount: "טעינת ספירת תיוגים גיאוגרפיים...",
    currentGeotaggedCount: "המספר העדכני של פריטים מתויגים גיאוגרפית: {count}.",
    geotaggerSearch: "חיפוש לפי מתייג",
    noCommentsYet: "אין עדיין תגובות",
    yourComment: "התגובה שלך…",
    logInToComment: "יש להתחבר כמשתמש רשום כדי להוסיף תגובה",
    addComment: "הוספת תגובה",
    removeQuestionableFlag: "הסרת סימון כמיקום לא ודאי",
    flagAsQuestionable: "סימון כמיקום לא ודאי",
    questionableLocation: "מיקום לא ודאי",
    markAsUnfit: "סימון כלא מתאים לתיוג",
    markAsTaggable: "סימון כמתאים לתיוג",
    unfitForTagging: "לא מתאים לתיוג",
    editLocation: "עריכת מיקום",
    removeLocation: "הסרת מיקום",
    addDescription: "+ הוספת תיאור",
    itemAwaitsGeotagging: "פריט זה עדיין ממתין לתיוג גיאוגרפי",
    viewSourceLeiden: "צפייה במקור באתר ספריות אוניברסיטת ליידן",
    viewSourceLoc: "צפייה במקור באתר ספריית הקונגרס",
    viewSourceTaArchive: "צפייה במקור באתר הארכיון העירוני תל אביב־יפו (יש לחפש לפי קוד הפריט שלמטה)",
    viewSourceTaArchiveDirect: "צפייה במקור באתר הארכיון העירוני תל אביב־יפו",
    viewSourceNationaalArchief: "צפייה במקור באתר הארכיון הלאומי ההולנדי",
    archiveItemCode: "קוד פריט בארכיון: {code}",
    copyrightAleksandrowicz: "© כל הזכויות שמורות ליורשי זאב אלכסנדרוביץ', תל אביב; את הסריקות ביצעה הספרייה הלאומית של ישראל",
    copyrightTaycm: "באדיבות ארכיון מוזיאון העיר תל אביב-יפו",
    itemId: "מזהה: {id}",
    firstGeotaggedBy: "תיוג גיאוגרפי ראשון על ידי {username}",
    lastGeotaggedBy: "תיוג גיאוגרפי אחרון על ידי {username}",
    removeGeotagConfirm: "להסיר את התיוג הגיאוגרפי מפריט זה? הוא יוחזר לקבוצת חסרי התיוג.",
    removeLocationError: "הסרת המיקום נכשלה: {error}",
    saveError: "נכשל: {error}",
    failedToSaveTitle: "שמירת הכותרת נכשלה: {error}",
    failedToSaveDescription: "שמירת התיאור נכשלה: {error}",
    photosForGeotagging: "תצלומים לתיוג גיאוגרפי",
    exitSelectMode: "יציאה ממצב בחירה",
    selectMultiple: "בחירה מרובה",
    taggable: "מתאים לתיוג",
    unfit: "לא מתאים לתיוג",
    searching: "מחפש…",
    searchResultCount: "{count} תוצאות",
    selectAll: "בחירת הכול",
    deselectAll: "ביטול בחירת הכול",
    restoreToTaggable: "החזרה לקבוצת המתאימים לתיוג",
    markAsUnfitForTagging: "סימון כלא מתאים לתיוג",
    addLocation: "הוסף מיקום",
    searchResults: "תוצאות חיפוש",
    itemsFound: "נמצאו {count} פריטים",
    pages: "{total}/{current} עמודים",
    batchOf: "קבוצה {current} מתוך {total}",
    itemsRange: "פריטים {from}–{to} מתוך {total}",
    historicalMapsAndImagery: "מפות היסטוריות ותצלומי אוויר",
    confirm: "אישור",
    suggestOnly: "הצעה בלבד",
    saveSuggestUncertain: "שמירת מיקום כלא ודאי",

    // Unified site
    allCreators: "כל היוצרים",
    creatorFilter: "סינון לפי יוצר",
    yearRangeFilter: "סינון לפי שנה",
    clearYearRange: "ניקוי סינון השנים",
    unifiedTitle: "היסטוריה מצולמת על המפה",
    aboutTooltip: "אודות האתר",

    termsOfUseTitle: "תנאי שימוש",
    termsOfUseText: "נתוני המיקום באתר — תיוגים גיאוגרפיים, כיווני צילום, כתובות, זיהויי מקומות, תרגומים והערות נוספות — הם פרי עבודתו של פרויקט מקום (MAQOM) ושל המתנדבים שתרמו לו.\nהנכם מוזמנים לעיין באתר לצרכים אישיים, חינוכיים ומחקריים. אין להוריד את הנתונים באופן אוטומטי או גורף, לרבות באמצעות סריקה אוטומטית (scraping), שליפה שיטתית מממשקי המפה או החיפוש של האתר, או כל דרך אחרת לחילוץ נתונים בכמויות.\nכל שימוש חוזר בנתונים, כולם או חלקם, מחוץ לאתר זה — לרבות פרסום מחדש, הפצה או שילוב ביישומון, באתר, במוצר או במאגר נתונים אחר — מחייב אישור מראש ובכתב. לבקשת אישור יש לפנות אל {email}.\nהתצלומים עצמם כפופים לתנאי השימוש של המוסדות המחזיקים בהם.",
    termsContactEmail: "oraleks@technion.ac.il",
  },
};

// Remote strings override — fetched from Supabase and cached in localStorage
const CACHE_KEY = 'ui_strings_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

let remoteStrings = { en: {}, he: {} };

// Load cached strings synchronously on module init
try {
  const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
  if (cached && cached.data && cached.ts > Date.now() - CACHE_TTL) {
    remoteStrings = cached.data;
  }
} catch (e) { /* ignore */ }

// Merged view: remote overrides local defaults
export const strings = {
  get en() { return { ...defaultStrings.en, ...remoteStrings.en }; },
  get he() { return { ...defaultStrings.he, ...remoteStrings.he }; },
};

/**
 * Fetch strings from Supabase and update cache.
 * Call once on app startup. Non-blocking — UI uses defaults until fetch completes.
 * Returns a promise that resolves when strings are loaded.
 */
export async function loadRemoteStrings() {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/read-api`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ action: 'get_ui_strings', params: {} }),
      }
    );
    if (!res.ok) return;
    const rows = await res.json();
    if (!Array.isArray(rows)) return;

    const en = {};
    const he = {};
    for (const row of rows) {
      if (row.en) en[row.key] = row.en;
      if (row.he) he[row.key] = row.he;
    }
    remoteStrings = { en, he };
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: remoteStrings, ts: Date.now() }));
  } catch (e) {
    // Silently fail — local defaults will be used
  }
}

/**
 * Get a translated string, with optional variable substitution.
 * Usage: t('itemsFound', lang, { count: 5, s: 's' })
 */
export function t(key, lang = 'en', vars = {}) {
  const str = strings[lang]?.[key] ?? strings.en[key] ?? key;
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(`{${k}}`, v),
    str
  );
}
